import React, { useEffect } from 'react'
import { generatePath } from 'react-router-dom'
import { DataCard, DataColumn, DataGrid } from '@/components/DataCard/DataCard'
import {
  FormField,
  FormReadonlyField,
  FormSelectField,
  FormTextareaField,
} from '@/components/Form/FormField'
import { Button } from '@/components/ui/button'
import { ClientDto } from 'api/Clients/Types/Client'
import { NameDto } from 'api/Common/Types'
import { PaymentMethodReplyDto } from 'api/PaymentProvider/Types/PaymentMethodReplyDto'
import { PaymentProviderFields } from 'api/PaymentProvider/Types/PaymentProviderFields'
import { DepositToWalletByPspTransaction } from 'api/Transaction/Types/DepositToWalletByPspTransaction'
import { TransactionsState } from 'api/Transaction/Types/TransactionsState'
import {
  CurrencyType,
  DragonPhoenixCurrencyType,
  PHPCurrencyType,
  WalletDto,
} from 'api/Wallet/Types/Wallet'
import { Link } from 'components/Link'
import { Loading } from 'components/Loading'
import PaymentServiceProviderFields from 'components/PaymentServiceProviderFields/PaymentServiceProviderFields'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'
import toNumber from 'lodash/toNumber'
import omitEmpty from 'omit-empty'
import { links } from 'utils/featureToggles'
import { FormSubmitValues } from 'utils/formValidation'

const mapFieldsToDetails = (options: { [key: string]: string | number }) =>
  Object.keys(options).map((f) => ({
    name: f,
    value: options[f],
  }))

function dataToSubmit(
  values: DepositFormValues,
  wallets: WalletDto[]
): FormSubmitValues<DepositToWalletByPspTransaction> {
  const { payment_currency_id: paymentCurrencyId, ...detailOptions } = omitEmpty(
    values.options
  ) as { [key: string]: string | number }
  const pspCurrencyId =
    (paymentCurrencyId as string | undefined) ||
    values.currency ||
    wallets.find((w) => w.id === values.walletId)?.currency.id ||
    ''
  return {
    amount: toNumber(values.amount),
    comment: values.comment,
    toWalletId: values.walletId as string,
    details: mapFieldsToDetails(detailOptions),
    pspCurrencyId,
    fromPspId: values.paymentProviderId as string,
    transactionStateId: values.transactionStateId,
  }
}

const DepositFormUI: React.FC<FormikProps<DepositFormValues> & OuterProps> = (props) => {
  const {
    client,
    handleSubmit,
    isSubmitting,
    values,
    errors,
    setFieldValue,
    isLoading,
    paymentProviderFields,
    walletId,
  } = props

  // effect for handle bug from backend, when we change payment provider which has custom payment_currency_id like USD
  // we have empty response, so that means we should set currency to currency as wallet is.
  useEffect(() => {
    if (walletId && !paymentProviderFields.length) {
      const wallet = props.wallets.find((w) => w.id === walletId)
      if (wallet) props.setCurrency(wallet.currency.id)
    }
  }, [walletId, paymentProviderFields]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // reset form on change client
    props.resetForm()
  }, [client?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Loading isLoading={isLoading}>
      <Form onSubmit={handleSubmit}>
        <DataGrid columns={1}>
          <DataColumn>
            <DataCard title='Deposit' columns={2}>
              <FormReadonlyField
                name='client'
                label='Client'
                value={values.client ?? ''}
                rightAddon={
                  <>
                    <Button type='button' size='sm' variant='outline' onClick={props.setClient}>
                      Select
                    </Button>
                    {client?.id && (
                      <Link
                        to={generatePath(links.recordsClients.detailsUrl as string, {
                          id: client.id,
                          slug: 'details',
                        })}
                        showIcon
                      />
                    )}
                  </>
                }
              />
              <FormSelectField
                name='walletId'
                label='Wallet'
                options={props.wallets.map((wallet) => ({
                  id: wallet.id,
                  name: `${wallet.name}-balance:${wallet.balance}`,
                }))}
                error={errors.walletId}
                onChange={(value) => {
                  const nextWalletId = value ? String(value) : ''
                  props.setFieldValue('walletId', nextWalletId)
                  props.setFieldValue('options.campaignId', null)
                  props.setWallet(nextWalletId)
                  props.getCampaignByWallet(nextWalletId)
                }}
                rightAddon={
                  values.walletId && (
                    <Link
                      to={generatePath(links.recordsWallets.detailsUrl as string, {
                        id: values.walletId,
                        slug: 'details',
                      })}
                      showIcon
                    />
                  )
                }
              />
              <FormSelectField
                name='paymentProviderId'
                label='Payment Provider'
                options={props.paymentProviders.map((paymentProvider) => ({
                  id: paymentProvider.method_id,
                  name: paymentProvider.method_name,
                }))}
                error={errors.paymentProviderId}
                onChange={(value) => {
                  const nextPaymentProviderId = value ? String(value) : ''
                  setFieldValue('paymentProviderId', nextPaymentProviderId)
                  props.getPaymentProviderFields(nextPaymentProviderId)
                }}
              />
              <FormSelectField
                name='options.campaignId'
                label='Campaign Account'
                options={props.campaigns.map((campaign) => ({
                  id: campaign.id,
                  name: `${campaign.tradingAccountName} - ${campaign.name}`,
                }))}
                onChange={(value) => {
                  const campaign = props.campaigns.find((item) => item.id === value)
                  if (campaign) {
                    setFieldValue('options.campaignId', campaign.id)
                    setFieldValue('options.tradingAccountId', campaign.tradingAccountId)
                  } else {
                    setFieldValue('options.campaignId', null)
                    setFieldValue('options.tradingAccountId', null)
                  }
                }}
              />
              <FormField
                name='amount'
                label='Amount'
                type='number'
                error={errors.amount}
                rightAddon={
                  props.currency ? (
                    <span className='inline-flex items-center px-3 text-sm text-muted-foreground'>
                      {props.currency}
                    </span>
                  ) : undefined
                }
              />
              <PaymentServiceProviderFields
                paymentProviderFields={props.paymentProviderFields}
                errors={errors}
                setFieldValue={setFieldValue}
                setCurrency={props.setCurrency}
              />
              <FormTextareaField name='comment' label='Comment' rows={3} maxLength={260} />
              <FormSelectField
                name='transactionStateId'
                label='Transaction State'
                options={[
                  { id: 0, name: 'Default' },
                  ...props.transactionState
                    .filter((state) => state.id >= 0 && state.id <= 300)
                    .filter((state) => state.id !== 100),
                ]}
                error={errors.transactionStateId}
                onChange={(value) => setFieldValue('transactionStateId', value ? Number(value) : 0)}
              />
            </DataCard>
          </DataColumn>
        </DataGrid>
        <div className='mt-4 flex justify-center'>
          <Button disabled={isSubmitting} type='submit'>
            Submit
          </Button>
        </div>
      </Form>
    </Loading>
  )
}

export interface DepositFormValues {
  client: string | null
  walletId: string | undefined
  amount: number
  comment: string
  paymentProviderId: string | undefined
  options: { [key: string]: string | number } | {}
  transactionStateId: number | null
  currency: string | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | null
}

interface OuterProps {
  isLoading: boolean
  client: ClientDto | null
  wallets: WalletDto[]
  paymentProviders: PaymentMethodReplyDto[]
  paymentProviderId: string
  paymentProviderFields: PaymentProviderFields[]
  transactionState: TransactionsState[]
  campaigns: CampaignWithTradingAccount[]
  currency: null | CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string
  walletId: string

  onSubmit(values: FormSubmitValues<DepositToWalletByPspTransaction>): Promise<void>
  setClient(): void
  setCurrency(currency: CurrencyType | DragonPhoenixCurrencyType | PHPCurrencyType | string): void
  getPaymentProviderFields(paymentProviderId: string): Promise<void>
  setWallet(walletId: string): void
  getCampaignByWallet(walletId: string): void
}

export type CampaignWithTradingAccount = Omit<NameDto, 'id'> & {
  tradingAccountName: string
  tradingAccountId: string
  id: string
}

export const DepositForm = withFormik<OuterProps, DepositFormValues>({
  mapPropsToValues: ({ client, walletId, currency, paymentProviderId }) => {
    const currentClient: Partial<ClientDto> = client || {}
    return {
      client: currentClient.name || null,
      options: {},
      transactionStateId: 0,
      walletId,
      currency,
      paymentProviderId,
      amount: 0,
      comment: '',
    }
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      await props.onSubmit(dataToSubmit(values, props.wallets))
    } finally {
      setSubmitting(false)
    }
  },
  validate: (values) => {
    const errors: FormikErrors<DepositFormValues> = {}
    if (values.transactionStateId == null) {
      errors.transactionStateId = 'Required'
    }
    if (!values.walletId) {
      errors.walletId = 'Required'
    }
    if (!values.amount) {
      errors.amount = 'Required'
    } else if (toNumber(values.amount) <= 0) {
      errors.amount = 'Must be greater than 0'
    }
    if (!values.paymentProviderId) {
      errors.paymentProviderId = 'Required'
    }
    return errors
  },
  enableReinitialize: true,
})(DepositFormUI)
